import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import Box from "../box";
import AvatarImage from "../avatar-image";
import { store } from "../../models/store";
import { avatarSize } from "./avatar.helper";
import { avatarSizeVar, avatar } from "./avatar.css";
import type { AvatarProps } from "./avatar.types";

function Avatar(props: AvatarProps) {
  const cleanupRef = useRef<() => void>(null);
  const [theme, setTheme] = useState(() => "light");

  const [isLoaded, setIsLoaded] = useState(() => false);

  useEffect(() => {
    setTheme(store.getState().theme);
    cleanupRef.current = store.subscribe((newState, prevState) => {
      setTheme(newState.theme);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (typeof cleanupRef.current === "function") cleanupRef.current();
    };
  }, []);

  return (
    <Box
      as="span"
      display="inline-flex"
      borderWidth={props.showBorder ? "$sm" : undefined}
      borderColor={props.borderColor}
      borderRadius={props.rounded ? "$full" : "none"}
      backgroundColor={props.backgroundColor}
      attributes={props.attributes}
      className={props.className}
    >
      <span
        data-loaded={isLoaded}
        data-custom-bg={!!props.backgroundColor}
        style={assignInlineVars({
          [avatarSizeVar]: `${avatarSize(props.size)}`,
        })}
        className={avatar[theme]}
      >
        <AvatarImage
          src={props.src}
          srcSet={props.srcSet}
          loading={props.loading}
          fallbackMode={props.fallbackMode}
          onLoad={(event) => {
            props.onLoad?.(event);
            setIsLoaded(true);
          }}
          width={avatarSize(props.size) as string}
          height={avatarSize(props.size) as string}
          onError={(event) => props.onError}
          getInitials={props.getInitials}
          name={props.name}
          borderRadius={props.borderRadius}
          ignoreFallback={props.ignoreFallback}
          crossOrigin={props.crossOrigin}
          referrerPolicy={props.referrerPolicy}
        />
        {props.children}
      </span>
    </Box>
  );
}

Avatar.defaultProps = { size: "md", rounded: true, fallbackMode: "initials" };

export default Avatar;
